/**
 * DaireChat Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Advanced AI-powered chat interface for the Irish Funds Member Portal with comprehensive features:
 * - Real-time streaming chat with multiple AI models (GPT-4.1, Grok, Deepseek, etc.)
 * - Dynamic document suggestions and contextual document analysis
 * - Smart source filtering and enhanced relevance scoring with IFIA brand colors
 * - Microsoft Speech SDK integration for conversation mode (voice input/output)
 * - Rich media support with PDF, News, Events, and Working Group modals
 * - Session persistence with automatic context saving/loading
 * - Advanced error handling and graceful degradation
 * - Production-ready with comprehensive accessibility features
 * - Responsive design supporting sidebar, fullscreen, and embedded modes
 */
import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef, useImperativeHandle, useCallback, useContext } from 'react';
import './DaireChat.css';
import { AuthContext } from '../Auth/AuthContext';
import apiHelper from '../../utils/apiHelper';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { saveChat, loadChats, loadChat, saveSessionContext, getSessionContext, clearSessionContext } from '../../utils/chatStorage';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faHouse, faSave, faFolderOpen, faEraser, faTrashAlt, faInfoCircle, faStop, faSearch, faQuestion, faBook, faFileAlt, faChartLine, faNewspaper, faGlobe, faCog, faExclamationCircle, faCheckCircle, faTimesCircle, faQuestionCircle, faKey, faTimes, faLightbulb, faMicrophone, faMicrophoneSlash, faChevronUp, faChevronDown, faCalendarAlt, faUsers, faClock, faMousePointer, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import PDFModal from '../../modals/PDFModal';
import NewsModal from '../../modals/NewsModal';
import { toast, ToastContainer } from 'react-toastify';
import { SpeechConfig, SpeechSynthesizer, SpeechRecognizer, ResultReason, AudioConfig } from 'microsoft-cognitiveservices-speech-sdk';
import EventsModal from '../../modals/EventsModal';
import InfoModal from '../../modals/InfoModal';
import { executeTopicFilter, getRedirectMessage, validateDaireConfig } from '../../config/daire-config';



function formatWorkingGroupOverview(text) {
  if (!text) return '';
  // Remove everything before [Summary]
  const summaryIndex = text.indexOf('[Summary]');
  let main = summaryIndex !== -1 ? text.slice(summaryIndex) : text;

  // Bold Summary: and add a break after the colon
  main = main.replace(/\[Summary\]/g, '<strong>Summary:</strong><br>');
  // Bold Terms of Reference: and add a break after the colon
  main = main.replace(/\[Terms of Reference\]/g, '<strong>Terms of Reference:</strong><br>');
  // Bold Objectives: and add a break after
  main = main.replace(/(Objectives:)/g, '<strong>$1</strong><br>');
  // Bold Updates: and add a break after
  main = main.replace(/(Updates:)/g, '<strong>$1</strong><br>');

  // Convert newlines to <br>
  main = main.replace(/\n/g, '<br>');

  return main;
}


function stripMarkdownForTTS(text) {
  if (!text) return '';
  // Remove bold, italics, headers, inline code, and links
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/__([^_]+)__/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1') // italics
    .replace(/_([^_]+)_/g, '$1') // italics
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/#+\s?([^\n]+)/g, '$1') // headers
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1') // links
    .replace(/\!\[(.*?)\]\((.*?)\)/g, '') // images
    .replace(/\n{2,}/g, '. ') // double newlines to pause
    .replace(/\n/g, ' ') // single newline to space
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim();
}

/**
 * DaireChat Component
 * 
 * @param {boolean} isOnDairePage - Whether the chat is displayed on the main Daire page
 * @param {Object} documentContext - Context for document-specific chat sessions
 * @param {boolean} hideFullscreen - Whether to hide fullscreen toggle button
 * @param {function} onNavigateHome - Callback when home navigation is requested
 * @param {boolean} fullPage - Whether chat is in full page mode
 * @param {function} onRelatedQuestionsUpdate - Callback when related questions are updated
 * @param {function} onUpdatingSummaryChange - Callback when summary update status changes
 * @param {function} onShowHelp - Callback when help is requested
 * @param {function} onTopicsUpdate - Callback when conversation topics are updated
 * @param {function} onMessagesUpdate - Callback when messages array changes
 * @param {function} onUpdatingTopicsChange - Callback when topic update status changes
 * @param {function} onWelcomeShown - Callback when welcome message is displayed
 * @param {boolean} externalShowDocumentSuggestions - External control for document suggestions visibility
 * @param {boolean} isSidebar - Whether chat is in sidebar mode (default: false)
 * @param {Object} ref - Forward ref for external component control
 */
const DaireChat = forwardRef(function DaireChat({ 
    isOnDairePage, 
    documentContext, 
    hideFullscreen, 
    onNavigateHome, 
    fullPage, 
    onRelatedQuestionsUpdate, 
    onUpdatingSummaryChange, 
    onShowHelp, 
    onTopicsUpdate, 
    onMessagesUpdate, 
    onUpdatingTopicsChange,
    onWelcomeShown,
    showDocumentSuggestions: externalShowDocumentSuggestions,
    isSidebar=false
}, ref) {
  const { user: [user] } = useContext(AuthContext);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  
  // Add profile data state
  const [profileData, setProfileData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { FILE_NAMES } = require('../../utils/constants');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [savedChats, setSavedChats] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasSavedChats, setHasSavedChats] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [lastSearchResults, setLastSearchResults] = useState(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const userWasAtBottomRef = useRef(true);
  const [selectedModel, setSelectedModel] = useState('gpt-4.1');
  const [truncatedContentFirst, setTruncatedContentFirst] = useState(false);
  const [maxDocs, setMaxDocs] = useState(50);
  const [conversationContext, setConversationContext] = useState({
    lastIntent: null,
    lastTopic: null,
    topics: [],
    summary: '',
    relatedQuestions: []
  });
  const [streamingState, setStreamingState] = useState({
    isSearching: false,
    documentsFound: 0,
    isAnalyzing: false,
    searchKeywords: []
  });

  const [tokenUsage, setTokenUsage] = useState({
    systemMessage: 0,
    userMessage: 0,
    documents: 0,
    total: 0
  });
  const [currentMessageTokens, setCurrentMessageTokens] = useState(null);
  const [sessionTotalTokens, setSessionTotalTokens] = useState(0);

  const [currentDocuments, setCurrentDocuments] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [clickedQuestions, setClickedQuestions] = useState(new Set());
  const [isUpdatingSummary, setIsUpdatingSummary] = useState(false);
  const [skipNextUpdate, setSkipNextUpdate] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [showQuickActionsInternal, setShowQuickActionsInternal] = useState(!documentContext?.isDocumentContext);
  const [modals, setModals] = useState({});
  const [allDocumentSources, setAllDocumentSources] = useState([]);
  const [sessionContext, setSessionContext] = useState({
    lastSearchEmbedding: null,
    lastSearchTimestamp: null
  });
  const [visibleSources, setVisibleSources] = useState(5);
  const [showSources, setShowSources] = useState(false);
  const [sourcesRef] = useState(() => React.createRef());
  const [flashSourcesButton, setFlashSourcesButton] = useState(false);
  const lastSourcesSignature = useRef('');
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [selectedSourceType, setSelectedSourceType] = useState('all');
  const [activeNewsId, setActiveNewsId] = useState(null);
  const [activeNewsType, setActiveNewsType] = useState('news');
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [shouldShowWelcome, setShouldShowWelcome] = useState(true);
  const [daireSuggestionsData, setDaireSuggestionsData] = useState([]);
  const [daireOptionsData, setDaireOptionsData] = useState(null);
  const [messageRatings, setMessageRatings] = useState({});
  const bottomRef = useRef(null);
  const [scrollToBottomNext, setScrollToBottomNext] = useState(false);
  const [activeWorkingGroup, setActiveWorkingGroup] = useState(null);
  const [showWorkingGroupModal, setShowWorkingGroupModal] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    show: false,
    data: null,
    type: 'workingGroup'
  });
  const [usedDocumentActions, setUsedDocumentActions] = useState(new Set());
  const [showDocumentSuggestions, setShowDocumentSuggestions] = useState(true);
  const [isConversationMode, setIsConversationMode] = useState(false);
  const isConversationModeRef = useRef(isConversationMode);
  const [isListening, setIsListening] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState(null);
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [conversationModeEnabled, setConversationModeEnabled] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [hasExistingMessages, setHasExistingMessages] = useState(false);
  const [speechQueue, setSpeechQueue] = useState([]);
  const speechQueueRef = useRef([]);
  const isSpeakingRef = useRef(false);
  // Add this near the top of the component
  const ttsBufferRef = useRef('');
  const activeSynthRef = useRef(null);
  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDisclaimer2Modal, setShowDisclaimer2Modal] = useState(false);
  const [showDaireInfoModal, setShowDaireInfoModal] = useState(false);
  
  // Handle ESC key to close disclaimer modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && showDisclaimer2Modal) {
        setShowDisclaimer2Modal(false);
      }
    };

    if (showDisclaimer2Modal) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [showDisclaimer2Modal]);

  const showHomeIcon = location.pathname !== '/daire' && !documentContext?.isDocumentContext;

    // Define startSpeechProcessor next
    const startSpeechProcessor = React.useCallback(() => {
      const processQueue = async () => {
        while (speechQueueRef.current.length > 0) {
          const next = speechQueueRef.current[0];
          await speakText(next);
          speechQueueRef.current = speechQueueRef.current.slice(1);
          setSpeechQueue([...speechQueueRef.current]); // update state for UI if needed
        }
        isSpeakingRef.current = false;
      };
      processQueue();
    }, []);

  // Initialize speech services
  useEffect(() => {
    if (isConversationMode) {
      setConversationModeEnabled(true);
      
      // Initialize Microsoft Speech Services
      const initSpeechServices = async () => {
        try {
          const speechConfig = SpeechConfig.fromSubscription(
            process.env.REACT_APP_SPEECH_KEY,
            process.env.REACT_APP_SPEECH_REGION
          );
          speechConfig.speechSynthesisVoiceName = "en-IE-ConnorNeural";
          
          const synthesizer = new SpeechSynthesizer(speechConfig);
          setSpeechSynthesis(synthesizer);

          // Initialize speech recognition
          const recognizer = new SpeechRecognizer(speechConfig);
          setSpeechRecognition(recognizer);
        } catch (error) {
          console.error('Speech services initialization failed:', error);
          toast.error('Failed to initialize speech services');
        }
      };

      initSpeechServices();
    }
  }, [isConversationMode]);

  // Cleanup speech services
  useEffect(() => {
    return () => {
      if (speechSynthesis) {
        speechSynthesis.close();
      }
      if (speechRecognition) {
        speechRecognition.close();
      }
    };
  }, [speechSynthesis, speechRecognition]);

  // Keep the ref in sync with state
  useEffect(() => {
    speechQueueRef.current = speechQueue;
  }, [speechQueue]);

  // Fetch member profile data
  const fetchMemberProfile = useCallback(async () => {
    if (user?.profile?.email) {
      try {
        // Check if API is ready before making calls
        const authStatus = apiHelper.checkAuthStatus();
        if (!authStatus.tokenGetterInitialized) {
          // Retry after a short delay
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 1000);
          return;
        }

        const profile = await apiHelper.getMemberProfileByEmail(user.profile.email);
       
        if (profile?.id) {
          setProfileData(profile);
        }
      } catch (error) {
        console.error('Error fetching member profile:', error);
        if (error.message.includes('404') && retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000);
        } else if (error.message.includes('Auth0 token getter not initialized') && retryCount < 5) {
          // Retry if auth isn't ready yet
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 1000);
        }
      }
    }
  }, [user?.profile?.email, retryCount]);

    useEffect(() => {
    fetchMemberProfile();
  }, [fetchMemberProfile]);

  // Update welcome message when profile data becomes available
  useEffect(() => {
    if (profileData && messages.length === 1 && messages[0]?.isWelcomeMessage && daireOptionsData) {
      const welcomeOption = daireOptionsData?.find(option => option.title === 'Welcome');
      const formattedName = getUserFirstName();

      const updatedWelcomeMessage = {
        type: 'bot',
        content: documentContext?.isDocumentContext ?
          `Hello ${formattedName}! I'm here to help you understand <strong>"${documentContext.title}"</strong>. What would you like to know about this document?` :
          formatWelcomeMessage(welcomeOption?.value, user, profileData),
        isWelcomeMessage: true
      };

      setMessages([updatedWelcomeMessage]);
    }
  }, [profileData, daireOptionsData, documentContext, user]);

  // Helper function to get the user's display name (profile name or Auth0 fallback)
  const getUserDisplayName = () => {
 
    if (profileData?.name) {
    
      return profileData.name;
    }
    const fallback = user?.profile?.name || '';
  
    return fallback;
  };

  // Helper function to get the user's first name for personalization
  const getUserFirstName = () => {
   
    if (profileData?.name) {
      const firstName = profileData.name.split(' ')[0];
      const result = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
     
      return result;
    }
    if (user?.profile?.name) {
      const result = user.profile.name.split(' ')[0].charAt(0).toUpperCase() + user.profile.name.split(' ')[0].slice(1).toLowerCase();
      return result;
    }

    return 'there';
  };
  
  // Function to handle speech recognition
  const startListening = async () => {
    if (!speechRecognition || !isConversationMode) return;

    try {
      setIsListening(true);
      const result = await speechRecognition.recognizeOnceAsync();
      if (result.reason === ResultReason.RecognizedSpeech) {
        const recognizedText = result.text;
        setInput(recognizedText);
        handleSubmit(null, recognizedText);
      }
    } catch (error) {
      console.error('Speech recognition error:', error);
    } finally {
      setIsListening(false);
    }
  };

  // Combine both effects into one
  useEffect(() => {
    const initializeChat = async () => {
      try {
        const savedContext = await getSessionContext();

        if (messages.length === 0 && shouldShowWelcome) {
          if (savedContext?.messages?.length) {
            setMessages(savedContext.messages);
            setConversationContext(savedContext.conversationContext || {
              lastIntent: null,
              lastTopic: null,
              topics: [],
              summary: '',
              relatedQuestions: []
            });
            setCurrentDocuments(savedContext.currentDocuments || null);
            setLastSearchResults(savedContext.lastSearchResults || null);
          } else {
            // Find the welcome message by filtering options by title
            const welcomeOption = daireOptionsData?.find(option => option.title === 'Welcome');
            const formattedName = getUserFirstName();

            const welcomeMessage = {
              type: 'bot',
              content: documentContext?.isDocumentContext ?
                `Hello ${formattedName}! I'm here to help you understand <strong>"${documentContext.title}"</strong>. What would you like to know about this document?` :
                formatWelcomeMessage(welcomeOption?.value, user, profileData),
              isWelcomeMessage: true // Add flag to identify welcome message
            };

            setMessages([welcomeMessage]);
            setShowQuickActionsInternal(!documentContext?.isDocumentContext);
            if (onWelcomeShown) {
              onWelcomeShown();
            }
          }
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
        // Find the welcome message by filtering options by title
        const welcomeOption = daireOptionsData?.find(option => option.title === 'Welcome');
        const formattedName = getUserFirstName();

        const welcomeMessage = {
          type: 'bot',
          content: documentContext?.isDocumentContext ?
            `Hello ${formattedName}! I'm here to help you understand <strong>"${documentContext.title}"</strong>. What would you like to know about this document?` :
            formatWelcomeMessage(welcomeOption?.value, user, profileData),
          isWelcomeMessage: true // Add flag to identify welcome message
        };

        setMessages([welcomeMessage]);
        setShowQuickActionsInternal(!documentContext?.isDocumentContext);
      }
    };

    if (user && daireOptionsData) {
      initializeChat();
    }
  }, [user, documentContext, messages.length, shouldShowWelcome, daireOptionsData, onWelcomeShown, profileData]);

  // Load session context on mount
  useEffect(() => {
    const loadContext = async () => {
      try {
        const savedContext = await getSessionContext();
        if (savedContext) {
          setMessages(savedContext.messages || []);
          setConversationContext(savedContext.conversationContext || {
            lastIntent: null,
            lastTopic: null,
            topics: [],
            summary: '',
            relatedQuestions: []
          });
          setCurrentDocuments(savedContext.currentDocuments || null);
          setLastSearchResults(savedContext.lastSearchResults || null);
        }
      } catch (error) {
        console.error('Error loading session context:', error);
      }
    };

    if (user) {
      loadContext();
    }
  }, [user]);

  // Listen for storage events to sync between instances
  useEffect(() => {
    const handleStorageChange = async (e) => {
      if (e.key === 'daire_session_context') {
        try {
          const savedContext = await getSessionContext();
          if (savedContext) {
            setMessages(savedContext.messages || []);
            setConversationContext(savedContext.conversationContext || {
              lastIntent: null,
              lastTopic: null,
              topics: [],
              summary: '',
              relatedQuestions: []
            });
            setCurrentDocuments(savedContext.currentDocuments || null);
            setLastSearchResults(savedContext.lastSearchResults || null);
          }
        } catch (error) {
          console.error('Error syncing session context:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Add effect to update quick actions when document context changes
  useEffect(() => {
    setShowQuickActionsInternal(!documentContext?.isDocumentContext);
  }, [documentContext]);

  useEffect(() => {
    const fetchFileInfoAndContent = async () => {
      try {
        // Validate Daire configuration on startup
        validateDaireConfig();

        const daireSuggestionsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.DAIRE_SUGGESTIONS);
        setDaireSuggestionsData(daireSuggestionsResult.fileContent);

        const daireOptionsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.DAIRE_OPTIONS);
        setDaireOptionsData(daireOptionsResult.fileContent);

      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchFileInfoAndContent();
  }, []);


  const isNewsQuery = (query) => {
    const newsKeywords = ['news', 'trending', 'latest', 'recent', 'update'];
    const queryLower = query.toLowerCase();
    return newsKeywords.some(keyword => queryLower.includes(keyword));
  };

  const handleQuickAction = async (query) => {

    // Handle ETF Intelligence navigation
    if (query === "ETF_INTELLIGENCE_NAVIGATE") {
      // Navigate to ETFBooks page and trigger ETF chat
      window.location.href = '/etfbook?openETFChat=true';
      return;
    }

    // CLIENT-SIDE NANO TOPIC FILTER FOR QUICK ACTIONS
    try {
      const filterResult = await executeTopicFilter(query);

      if (!filterResult.allowed) {
        // Add user message and redirect response immediately
        const redirectMessage = getRedirectMessage('standard');
        const newMessagesWithRedirect = [
          ...messages,
          { type: 'user', content: query },
          { type: 'bot', content: redirectMessage, isRedirectMessage: true }
        ];
        
        setMessages(newMessagesWithRedirect);
        setShowSources(false);
        
        return; // Stop here, don't proceed to backend
      }
      
    } catch (error) {
      // Continue with normal flow if filter fails
    }

    try {
        setIsProcessing(true);
        setShowSources(false);

        const authToken = await apiHelper.getAuthToken();
        const controller = new AbortController();
        setAbortController(controller);
      
        const newMessages = [...messages, { type: 'user', content: query }];
        setMessages(newMessages);

        // Check if we're in document context
        if (documentContext?.listItemId || documentContext?.url) {
            const response = await fetch(`${process.env.REACT_APP_API_URL}daire/documentSuggestions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    listItemId: Number(documentContext.listItemId),
                    documentName: documentContext.title,
                    query: query,
                    userName: getUserDisplayName(),
                    userEmail: user?.profile?.email,
                    url: documentContext.url,
                    aiClient: selectedModel,
                    truncatedContentFirst: truncatedContentFirst
                }),
                signal: controller.signal
            });
                    
            if (!response.ok) throw new Error('Network response was not ok');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let content = '';
            let buffer = '';
            let responseId = null;

                            while (true) {
                    const { value, done } = await reader.read();
                    if (done) {
                        // Streaming completed
                        break;
                    }

                const chunk = decoder.decode(value);
                buffer += chunk;
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(5);
                        if (data === '[DONE]') continue;

                        try {
                            const parsed = JSON.parse(data);
                          
                            if (parsed.type === 'documents') {
                                setAllDocumentSources(parsed.documents || []);
                              
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastMessage = updated[updated.length - 1];
                                    if (lastMessage?.type === 'bot') {
                                        lastMessage.documents = parsed.documents;
                                    } else {
                                        updated.push({ 
                                            type: 'bot', 
                                            content: content,
                                            documents: parsed.documents
                                        });
                                    }
                                    return updated;
                                });
                            } else if (parsed.content) {
                                content += parsed.content;
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastMessage = updated[updated.length - 1];
                                    if (lastMessage?.type === 'bot') {
                                        lastMessage.content = content;
                                    } else {
                                        updated.push({ type: 'bot', content: content });
                                    }
                                    return updated;
                                });
                                
                            } else if (parsed.type === 'usage_tracked') {
                                responseId = parsed.listId;
                                // Update the message state with the response ID and token info
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastMessage = updated[updated.length - 1];
                                    if (lastMessage?.type === 'bot') {
                                        lastMessage.listId = responseId;
                                        lastMessage.totalTokens = parsed.totalTokens;
                                    }
                                    return updated;
                                });
                                // Set current message tokens for display
                                if (parsed.totalTokens) {
                                    setCurrentMessageTokens(parsed.totalTokens);
                                    setSessionTotalTokens(prev => prev + parsed.totalTokens);
                                }
                            }

                            // Update token usage when documents are received
                            if (parsed.type === 'documents' && parsed.tokenUsage) {
                                setTokenUsage(parsed.tokenUsage);
                            }
                        } catch (error) {
                            console.error('Error parsing SSE data:', error);
                        }
                    }
                }
            }
        } else {
            // Use the general daireintent endpoint for non-document queries
            const isNews = isNewsQuery(query);
            const requestBody = {
                messages: newMessages.slice(-10).map(msg => ({
                    role: msg.type === 'user' ? 'user' : 'assistant',
                    content: msg.content
                })),
                userName: getUserDisplayName(),
                userEmail: user?.profile?.email,
                documentContext: sessionContext,
                typeFilter: isNews ? 'news' : undefined,
                model: selectedModel,
                maxDocs: maxDocs
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}daire/daireintent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(requestBody),
                signal: controller.signal
            });
            if (!response.ok) throw new Error('Network response was not ok');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let content = '';
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                buffer += chunk;

                let eventEnd;
                while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
                    const event = buffer.slice(0, eventEnd);
                    buffer = buffer.slice(eventEnd + 2);
                    if (event.startsWith('data: ')) {
                        const data = event.slice(6);
                        if (data.trim() === '[DONE]') {
                            if (isConversationMode && ttsBufferRef.current) {
                              speakText(stripMarkdownForTTS(ttsBufferRef.current));
                              ttsBufferRef.current = '';
                            }
                            continue;
                        }
                        try {
                            const parsed = JSON.parse(data);

                            switch (parsed.type) {
                                case 'keywords':
                                    setStreamingState(prev => ({
                                        ...prev,
                                        searchKeywords: parsed.keywords
                                    }));
                                    break;

                                case 'topics':
                                    setConversationContext(prev => ({
                                        ...prev,
                                        topics: parsed.topics || []
                                    }));
                                    if (onUpdatingTopicsChange) onUpdatingTopicsChange(false);
                                    break;

                                case 'document_count':
                                    setStreamingState(prev => ({
                                        ...prev,
                                        isSearching: false,
                                        documentsFound: parsed.count,
                                        isAnalyzing: true
                                    }));
                                    break;

                                case 'content':
                                    content += parsed.content;
                                    if (isConversationMode) {
                                        // Add to speech buffer instead of speaking immediately
                                        setSpeechQueue(prev => [...prev, parsed.content]);
                                    }
                                    setMessages(prev => {
                                        const updated = [...prev];
                                        const lastMessage = updated[updated.length - 1];
                                        if (lastMessage?.type === 'bot') {
                                            lastMessage.content = content;
                                        } else {
                                            updated.push({ type: 'bot', content: content });
                                        }
                                        return updated;
                                    });
                                    
                                    break;

                                case 'documents':
                                    setAllDocumentSources(parsed.documents || []);
                                   
                                    setMessages(prev => {
                                        const updated = [...prev];
                                        const lastMessage = updated[updated.length - 1];
                                        if (lastMessage?.type === 'bot') {
                                            lastMessage.documents = parsed.documents;
                                        } else {
                                            updated.push({
                                                type: 'bot',
                                                content: content,
                                                documents: parsed.documents
                                            });
                                        }
                                        return updated;
                                    });
                                    break;

                                case 'usage_tracked':
                                    
                                    // Update the message state immediately with the listId and token info
                                    setMessages(prev => {
                                        const updated = [...prev];
                                        const lastMessage = updated[updated.length - 1];
                                        if (lastMessage?.type === 'bot') {
                                            lastMessage.listId = parsed.listId;
                                            lastMessage.totalTokens = parsed.totalTokens;
                                        }
                                        return updated;
                                    });
                                    // Set current message tokens for display
                                    if (parsed.totalTokens) {
                                        setCurrentMessageTokens(parsed.totalTokens);
                                        setSessionTotalTokens(prev => prev + parsed.totalTokens);
                                    }
                                    break;

                                case 'error':
                                    setMessages(prev => [...prev, {
                                        type: 'error',
                                        content: parsed.error || 'An error occurred while processing your request.'
                                    }]);
                                    break;
                            }

                            // Update token usage when documents are received
                            if (parsed.type === 'documents' && parsed.tokenUsage) {
                                setTokenUsage(parsed.tokenUsage);
                            }
                            if (parsed.content) {
                              ttsBufferRef.current += parsed.content;
                            }
                        } catch (error) {
                            console.error('Failed to parse SSE data:', error);
                        }
                    }
                }
            }
        }

    } catch (error) {
        setMessages(prev => [...prev, {
            type: 'error',
            content: 'Sorry, there was an error processing your message.'
        }]);
    } finally {
        setIsProcessing(false);
    }
  };

  const QuickActions = () => {
    if (messages.length > 1 || !showQuickActionsInternal) return null;

    const getIconComponent = (iconName) => {
      if (!iconName) return null;

      // Map icon names to FontAwesome icons
      const iconMap = {
        'search': faSearch,
        'question': faQuestion,
        'book': faBook,
        'file': faFileAlt,
        'chart': faChartLine,
        'news': faNewspaper,
        'globe': faGlobe,
        'settings': faCog,
        'info': faInfoCircle,
        'warning': faExclamationCircle,
        'success': faCheckCircle,
        'error': faTimesCircle
      };

      const icon = iconMap[iconName];
      if (icon) {
        return <FontAwesomeIcon icon={icon} />;
      }
      return <span className="custom-icon-text">{iconName}</span>;
    };

    // Get current month and year
    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
    const currentYear = currentDate.getFullYear();
    const priorMonthDate = new Date(currentDate);
    priorMonthDate.setMonth(currentDate.getMonth() - 1);
    const priorMonthName = priorMonthDate.toLocaleString('default', { month: 'long' });
    // Add ETF Intelligence suggestion
    const etfSuggestion = {
      title: "ETF Intelligence",
      icon: "chart",
      query: "ETF_INTELLIGENCE_NAVIGATE"
    };

    return (
      <div className={`suggestion-boxes${isOnDairePage ? ' is-on-daire-page' : ''}`}>
        {/* ETF Intelligence suggestion */}
        
        
        {/* Dynamic suggestions */}
        {daireSuggestionsData.map((action, index) => {
          // Replace placeholders in the title
          const formattedTitle = action.title
            .replaceAll('{Month}', currentMonth)
            .replaceAll('{Year}', currentYear)
            .replaceAll('{PriorMonth}', priorMonthName);
          // Replace placeholders in the query
          const formattedQuery = action.query
            .replaceAll('{Month}', currentMonth)
            .replaceAll('{Year}', currentYear)
            .replaceAll('{PriorMonth}', priorMonthName);

          return (
            <Tippy
              key={index}
              content={formattedQuery}
              placement="top"
              delay={[500, 0]}
            >
              <button
                className="suggestion-box"
                onClick={() => handleQuickAction(formattedQuery)}
              >
                <span className="suggestion-icon">
                  {getIconComponent(action.icon)}
                </span>
                <span className="suggestion-text">{formattedTitle}</span>
              </button>
            </Tippy>
          );
        })}

        {process.env.REACT_APP_SHOW_ETF_DAIRE_INTELLIGENCE === 'true' && (
          <Tippy
            content="Navigate to ETF Intelligence"
            placement="top"
            delay={[500, 0]}
          >
            <button
              className="suggestion-box etf-intelligence-suggestion"
              onClick={() => handleQuickAction(etfSuggestion.query)}
            >
              <span className="suggestion-icon">
                {getIconComponent(etfSuggestion.icon)}
              </span>
              <span className="suggestion-text">{etfSuggestion.title}</span>
            </button>
          </Tippy>
        )}

        {/* <button
            className="suggestion-box"
            onClick={() => handleQuickAction("What's trending in Irish Funds news June 2025?")}
          >
            <span className="suggestion-icon">
              {getIconComponent("chart")}
            </span>
            <span className="suggestion-text">What's trending in Irish Funds news June 2025?</span>
          </button> */}
      </div>
    );
  };

  // Save context whenever relevant state changes
  useEffect(() => {
    const saveContext = async () => {
      try {
        await saveSessionContext({
          messages,
          conversationContext,
          currentDocuments,
          lastSearchResults
        });
      } catch (error) {
        console.error('Error saving session context:', error);
      }
    };

    if (messages.length > 0) {
      saveContext();
    }
  }, [messages, conversationContext, currentDocuments, lastSearchResults]);

  // Clear context when component unmounts
  useEffect(() => {
    return () => {
      clearSessionContext().catch(error => {
        console.error('Error clearing session context:', error);
      });
    };
  }, []);

  // Before messages update, capture if user is at bottom
  useLayoutEffect(() => {
    if (chatWindowRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
      userWasAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 30;
    }
  }, [messages.length]);

  // Only auto-scroll if user was at bottom before new message
  useEffect(() => {
    if (chatWindowRef.current && userWasAtBottomRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages]);

  const loadSavedChats = async () => {
    try {
      const chats = await loadChats();
      const sortedChats = chats.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSavedChats(sortedChats);
      setHasSavedChats(sortedChats.length > 0);
    } catch (error) {
      setHasSavedChats(false);
    }
  };

  const handleSaveChat = async () => {
    try {
      const savedChat = await saveChat(messages);
  
      await loadSavedChats();

      // Extract the first few words of the first user message for the title
      const chatTitle = messages.find(m => m.type === 'user')?.content.split(' ').slice(0, 5).join(' ') + '...';

      toast.success(`Chat "${chatTitle}" saved successfully!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error('Failed to save chat');
    }
  };

  const handleLoadChat = async (id) => {
    try {
      const chat = await loadChat(id);
      setMessages(chat.messages);
      setShowChatModal(false);
      // Reset document context and show quick actions
      setShowQuickActionsInternal(true);
      setUsedDocumentActions(new Set());
      // Clear any document context
      if (documentContext?.isDocumentContext) {
        // Just reset the context without navigating
        setShowDocumentSuggestions(false);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const handleSubmit = async (e, overrideInput) => {
    if (e) e.preventDefault();
    const messageToSend = overrideInput !== undefined ? overrideInput : input;
    if (!messageToSend.trim() || isProcessing) return;

    // Set first message flag to false when user sends a message
    if (isFirstMessage) {
      setIsFirstMessage(false);
    }

    // CLIENT-SIDE NANO TOPIC FILTER 
    try {
      const filterResult = await executeTopicFilter(messageToSend);

      if (!filterResult.allowed) {
        // Add user message and redirect response immediately
        const redirectMessage = getRedirectMessage('standard');
        const newMessagesWithRedirect = [
          ...messages,
          { type: 'user', content: messageToSend },
          { type: 'bot', content: redirectMessage, isRedirectMessage: true }
        ];
        
        setMessages(newMessagesWithRedirect);
        setInput('');
        setShowSources(false);
        
        return; // Stop here, don't proceed to backend
      }
      
    } catch (error) {
      // Continue with normal flow if filter fails
    }

    setInput('');
    setShowSources(false);

    const newMessages = [...messages, { type: 'user', content: messageToSend }];
    setMessages(newMessages);
    setIsProcessing(true);

    const controller = new AbortController();
    setAbortController(controller);

    try {
        

        const authToken = await apiHelper.getAuthToken();
        
        if (documentContext?.listItemId || documentContext?.url) {
            const response = await fetch(`${process.env.REACT_APP_API_URL}daire/documentSuggestions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    listItemId: Number(documentContext.listItemId),
                    documentName: documentContext.title,
                    query: messageToSend,
                    userName: getUserDisplayName(),
                    userEmail: user?.profile?.email,
                    url: documentContext.url,
                    aiClient: selectedModel,
                    truncatedContentFirst: truncatedContentFirst
                }),
                signal: controller.signal
            });
            if (!response.ok) throw new Error('Network response was not ok');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let content = '';
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                buffer += chunk;
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(5);
                        if (data === '[DONE]') continue;

                        try {
                            const parsed = JSON.parse(data);

                            if (parsed.type === 'documents') {
                                setAllDocumentSources(parsed.documents || []);
                             
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastMessage = updated[updated.length - 1];
                                    if (lastMessage?.type === 'bot') {
                                        lastMessage.documents = parsed.documents;
                                    } else {
                                        updated.push({ 
                                            type: 'bot', 
                                            content: content,
                                            documents: parsed.documents
                                        });
                                    }
                                    return updated;
                                });
                            } else if (parsed.content) {
                                content += parsed.content;
                                if (isConversationMode) {
                                    // Add to speech buffer instead of speaking immediately
                                    setSpeechQueue(prev => [...prev, parsed.content]);
                                }
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastMessage = updated[updated.length - 1];
                                    if (lastMessage?.type === 'bot') {
                                        lastMessage.content = content;
                                    } else {
                                        updated.push({ type: 'bot', content: content });
                                    }
                                    return updated;
                                });

                            } else if (parsed.type === 'usage_tracked') {
                                // Update the message state with the response ID and token info
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastMessage = updated[updated.length - 1];
                                    if (lastMessage?.type === 'bot') {
                                        lastMessage.listId = parsed.listId;
                                        lastMessage.totalTokens = parsed.totalTokens;
                                    }
                                    return updated;
                                });
                                // Set current message tokens for display
                                if (parsed.totalTokens) {
                                    setCurrentMessageTokens(parsed.totalTokens);
                                    setSessionTotalTokens(prev => prev + parsed.totalTokens);
                                }
                            }

                            // Update token usage when documents are received
                            if (parsed.type === 'documents' && parsed.tokenUsage) {
                                setTokenUsage(parsed.tokenUsage);
                            }
                        } catch (error) {
                            console.error('Error parsing SSE data:', error);
                        }
                    }
                }
            }
      
        } else {
            // Single API call to daireintent
            const isNews = isNewsQuery(messageToSend);
            const requestBody = {
                messages: newMessages.slice(-10).map(msg => ({
                    role: msg.type === 'user' ? 'user' : 'assistant',
                    content: msg.content
                })),
                userName: getUserDisplayName(),
                userEmail: user?.profile?.email,
                documentContext: {
                    ...sessionContext,
                    userEmail: user?.profile?.email
                },
                typeFilter: isNews ? 'news' : undefined,
                model: selectedModel,
                truncatedContentFirst: truncatedContentFirst,
                maxDocs: maxDocs
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}daire/daireintent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(requestBody),
                signal: controller.signal
            });
            if (!response.ok) throw new Error('Network response was not ok');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let content = '';
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                buffer += chunk;

                let eventEnd;
                while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
                    const event = buffer.slice(0, eventEnd);
                    buffer = buffer.slice(eventEnd + 2);
                    if (event.startsWith('data: ')) {
                        const data = event.slice(6);
                        if (data.trim() === '[DONE]') {
                            if (isConversationMode && ttsBufferRef.current) {
                              speakText(stripMarkdownForTTS(ttsBufferRef.current));
                              ttsBufferRef.current = '';
                            }
                            continue;
                        }
                        try {
                            const parsed = JSON.parse(data);
                           
                            switch (parsed.type) {
                                case 'keywords':
                                    setStreamingState(prev => ({
                                        ...prev,
                                        searchKeywords: parsed.keywords
                                    }));
                                    break;

                                case 'topics':
                                    setConversationContext(prev => ({
                                        ...prev,
                                        topics: parsed.topics || []
                                    }));
                                    if (onUpdatingTopicsChange) onUpdatingTopicsChange(false);
                                    break;

                                case 'document_count':
                                    setStreamingState(prev => ({
                                        ...prev,
                                        isSearching: false,
                                        documentsFound: parsed.count,
                                        isAnalyzing: true
                                    }));
                                    break;

                                case 'content':
                                    content += parsed.content;
                                    if (isConversationMode) {
                                        // Add to speech buffer instead of speaking immediately
                                        setSpeechQueue(prev => [...prev, parsed.content]);
                                    }
                                    setMessages(prev => {
                                        const updated = [...prev];
                                        const lastMessage = updated[updated.length - 1];
                                        if (lastMessage?.type === 'bot') {
                                            lastMessage.content = content;
                                        } else {
                                            updated.push({ type: 'bot', content: content });
                                        }
                                        return updated;
                                    });
                                    
                                    break;

                                case 'documents':
                                    setAllDocumentSources(parsed.documents || []);
                                   
                                    setMessages(prev => {
                                        const updated = [...prev];
                                        const lastMessage = updated[updated.length - 1];
                                        if (lastMessage?.type === 'bot') {
                                            lastMessage.documents = parsed.documents;
                                        } else {
                                            updated.push({
                                                type: 'bot',
                                                content: content,
                                                documents: parsed.documents
                                            });
                                        }
                                        return updated;
                                    });
                                    break;

                                case 'error':
                                    setMessages(prev => [...prev, {
                                        type: 'error',
                                        content: parsed.error || 'An error occurred while processing your request.'
                                    }]);
                                    break;

                                case 'usage_tracked':
                                    
                                    // Update the message state immediately with the listId and token info
                                    setMessages(prev => {
                                        const updated = [...prev];
                                        const lastMessage = updated[updated.length - 1];
                                        if (lastMessage?.type === 'bot') {
                                            lastMessage.listId = parsed.listId;
                                            lastMessage.totalTokens = parsed.totalTokens;
                                        }
                                        return updated;
                                    });
                                    // Set current message tokens for display
                                    if (parsed.totalTokens) {
                                        setCurrentMessageTokens(parsed.totalTokens);
                                        setSessionTotalTokens(prev => prev + parsed.totalTokens);
                                    }
                                    break;
                            }

                            // Update token usage when documents are received
                            if (parsed.type === 'documents' && parsed.tokenUsage) {
                                setTokenUsage(parsed.tokenUsage);
                            }
                            if (parsed.content) {
                              ttsBufferRef.current += parsed.content;
                            }
                        } catch (error) {
                            // Failed to parse SSE data
                        }
                    }
                }
            }
       
        }

    } catch (error) {
        if (!controller.signal.aborted) {
            setMessages(prev => [...prev, {
                type: 'error',
                content: 'Sorry, there was an error processing your message.'
            }]);
        }
    } finally {
        setIsProcessing(false);
        setAbortController(null);
    }

  };

  const handleStop = () => {
    if (speechSynthesis) {
      speechSynthesis.close();
    }
    if (speechRecognition) {
      speechRecognition.close();
    }
    setIsProcessing(false);
    setIsListening(false);
    setStreamingState(prev => ({
      ...prev,
      isStreaming: false,
      isSearching: false,
      isAnalyzing: false,
      documentsFound: 0
    }));
  };

  // Add ref for debounce timer
  const debounceTimer = useRef(null);

  // Update the effect that triggers summary updates
  useEffect(() => {
    if (messages.length > 0 && !isProcessing) {
      if (skipNextUpdate) {
        setSkipNextUpdate(false);
        return;
      }
      debounceTimer.current = setTimeout(() => {
        updateConversationSummary();
      }, 2000);
      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }
  }, [messages, isProcessing, skipNextUpdate]);

  const updateConversationSummary = async () => {
    // Don't generate questions if not enough messages, or in sidebar mode
    if (messages.length < 3 || !isOnDairePage) return;
    try {
      setIsUpdatingSummary(true);
      if (onUpdatingTopicsChange) onUpdatingTopicsChange(true);
      const authToken = await apiHelper.getAuthToken();
      const recentMessages = messages.slice(-3);
      const response = await fetch(`${process.env.REACT_APP_API_URL}daire/getQuestionsandTopics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          messages: recentMessages,
          userName: getUserDisplayName(),
          currentContext: {
            lastIntent: conversationContext.lastIntent,
            lastTopic: conversationContext.lastTopic,
            shouldRefreshQuestions: true,
            forceRefresh: true
          }
        })
      });
      if (!response.ok) throw new Error('Failed to summarise conversation');
      const data = await response.json();
      if (data.relatedQuestions?.length > 0) {
        setConversationContext(prev => ({
          ...prev,
          summary: data.summary,
          topics: data.topics || [],
          relatedQuestions: data.relatedQuestions
        }));
      }
    } catch (error) {
      // Error updating conversation summary
    } finally {
      setIsUpdatingSummary(false);
      if (onUpdatingTopicsChange) onUpdatingTopicsChange(false);
    }
  };

  const handleClearChat = async () => {
    try {
      // Reset all state
      setMessages([]);
      setCurrentDocuments(null);
      setClickedQuestions(new Set());
      setConversationContext({
        lastIntent: null,
        lastTopic: null,
        topics: [],
        summary: '',
        relatedQuestions: []
      });
      setShowQuickActionsInternal(!documentContext?.isDocumentContext);
      setAllDocumentSources([]);
      setMessageRatings({}); // Reset message ratings
      setUsedDocumentActions(new Set()); // Reset used document actions
      // Reset token usage
      setTokenUsage({
        systemMessage: 0,
        userMessage: 0,
        documents: 0,
        total: 0
      });
      setCurrentMessageTokens(null);
      setSessionTotalTokens(0);

      // Force welcome message to show
      setShouldShowWelcome(true);

      try {
        await clearSessionContext();
      } catch (error) {
        // Error clearing session context
      }
    } catch (error) {
      // Error in handleClearChat
    }
  };

  // Add this components configuration
  const components = {
    // Handle code blocks with syntax highlighting
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  // Add fullscreen change listener (only for sidebar mode)
  useEffect(() => {
    if (!isSidebar) return; // Only listen for browser fullscreen in sidebar mode
    
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [isSidebar]);

  // Add ESC key handler for fullscreen
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        // Let the browser handle ESC for fullscreen exit
        return;
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    if (isOnDairePage) {
      // On DairePage, use browser fullscreen API like everywhere else
      if (!isFullscreen) {
        setIsFullscreen(true);
        setTimeout(() => {
          const targetElement = document.querySelector('.daira-chat') || document.body;
          if (targetElement && targetElement.requestFullscreen) {
            targetElement.requestFullscreen().catch(err => {
              console.warn('Could not enter fullscreen:', err);
            });
          } else if (targetElement && targetElement.webkitRequestFullscreen) {
            targetElement.webkitRequestFullscreen();
          } else if (targetElement && targetElement.msRequestFullscreen) {
            targetElement.msRequestFullscreen();
          }
        }, 50);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(err => {
            console.warn('Could not exit fullscreen:', err);
          });
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } else if (isSidebar) {
      // In sidebar, use browser fullscreen API
      if (!isFullscreen) {
        // First set the state to trigger re-render with fullscreen layout
        setIsFullscreen(true);
        
        // Then request fullscreen on the sidebar element
        setTimeout(() => {
          const targetElement = document.querySelector('.daire-sidebar-v2') || document.body;
          if (targetElement && targetElement.requestFullscreen) {
            targetElement.requestFullscreen().catch(err => {
              console.warn('Could not enter fullscreen:', err);
            });
          } else if (targetElement && targetElement.webkitRequestFullscreen) {
            targetElement.webkitRequestFullscreen();
          } else if (targetElement && targetElement.msRequestFullscreen) {
            targetElement.msRequestFullscreen();
          }
        }, 50);
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(err => {
            console.warn('Could not exit fullscreen:', err);
          });
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } else {
      // Default behavior for other contexts
      setIsFullscreen(!isFullscreen);
    }
  };

  // Add this effect to check for saved chats on mount and when messages change
  useEffect(() => {
    const checkSavedChats = async () => {
      try {
        const chats = await loadChats();
        const sortedChats = chats.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setSavedChats(sortedChats);
        setHasSavedChats(sortedChats.length > 0);
          } catch (error) {
      setHasSavedChats(false);
    }
    };

    checkSavedChats();
  }, [messages]); // Re-run when messages change

  // Update the hasChatToSave logic to be more lenient
  const hasChatToSave = messages.length > 0 && messages.some(msg => msg.type === 'user');

  // Update the scroll effect
  useEffect(() => {
    if (chatWindowRef.current && shouldAutoScroll && !isProcessing) {
      requestAnimationFrame(() => {
        // Double-check that the ref is still valid in the animation frame
        if (chatWindowRef.current) {
          chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
        }
      });
    }
  }, [messages, shouldAutoScroll]);

  const StreamingStatus = ({ state }) => {
    let statusText = '';
    if (state.isSearching) {
      statusText = 'Searching documents...';
    } else if (state.isAnalyzing) {
      statusText = 'Analyzing content...';
    } else if (isProcessing) {
      statusText = 'Processing...';
    }
    return (
      <div className="streaming-status">
        {(state.isSearching || state.isAnalyzing || isProcessing) && (
          <>
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
            <span className="status-text">{statusText}</span>
          </>
        )}
      </div>
    );
  };


  // Add copy handler
  const handleCopy = async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Update toolbar
  const renderToolbar = () => (
    <div className="chat-toolbar">
      <div className="toolbar-left">
        {showHomeIcon && (
          <Link
            to="/daire"
            className="toolbar-button daire-page-link"
            title="Go to Daire Page"
            onClick={(e) => {
              if (onNavigateHome) {
                onNavigateHome();
              }
            }}
          >
            <FontAwesomeIcon icon={faHouse} />
          </Link>
        )}
        {currentMessageTokens && (
          <Tippy content="Tokens used for the last AI response. Tokens are units of text that the AI processes - roughly 4 characters per token.">
            <div className="token-usage-display">
              <span className="token-count">{currentMessageTokens.toLocaleString()}</span>
              <span className="token-label">tokens</span>
            </div>
          </Tippy>
        )}
        {sessionTotalTokens > 0 && (
          <Tippy content="Total tokens used in this chat session. Each question and response consumes tokens for processing your queries and generating responses.">
            <div className="session-token-usage-display">
              <span className="session-token-count">{sessionTotalTokens.toLocaleString()}</span>
              <span className="session-token-label">total</span>
            </div>
          </Tippy>
        )}
        {/* <div className="model-selector">
          <Tippy content="Select the model you want to use for document search, gpt-4.1-mini is always used for casual interaction and follow ups.">
          <select 
            value={selectedModel} 
            onChange={(e) => setSelectedModel(e.target.value)}
            className="model-select"
          >
            <option value="gpt-4.1">GPT-4.1</option>
            <option value="gpt-4.1-mini">GPT-4.1-Mini</option>
            <option value="grok-3-mini">Grok 3 Mini</option>
            <option value="mistral-medium">Mistral-Medium</option>
            <option value="deepseek">Deepseek</option>
            <option value="phi-4">Phi-4</option>
            </select>
            </Tippy>
        </div> */}


      </div>
      <div className="toolbar-right">

        

        {!hideFullscreen && (
          <Tippy content={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            <button onClick={toggleFullscreen} className="toolbar-button fullscreen">
              {isFullscreen ? (
                <FontAwesomeIcon icon={faCompress} />
              ) : (
                <FontAwesomeIcon icon={faExpand} />
              )}
            </button>
          </Tippy>
        )}

        {documentContext?.isDocumentContext && !showDocumentSuggestions && usedDocumentActions.size < 3 && (
          <Tippy content="Show Suggestions">
            <button 
              onClick={() => setShowDocumentSuggestions(true)} 
              className="toolbar-button show-suggestions"
            >
              <FontAwesomeIcon icon={faLightbulb} />
            </button>
          </Tippy>
        )}
        {hasChatToSave && (
          <Tippy content="Save Chat">
            <button onClick={handleSaveChat} className="toolbar-button">
              <FontAwesomeIcon icon={faSave} />
            </button>
          </Tippy>
        )}
        {hasSavedChats && !documentContext?.isDocumentContext && (
          <Tippy content="Open Chat">
            <button onClick={() => setShowChatModal(true)} className="toolbar-button">
              <FontAwesomeIcon icon={faFolderOpen} />
            </button>
          </Tippy>
        )}
        {hasChatToSave && (
          <Tippy content="Clear Chat">
            <button onClick={handleClearChat} className="toolbar-button">
              <FontAwesomeIcon icon={faEraser} />
            </button>
          </Tippy>
        )}
        <div style={{display:'none'}}>
        {/* Conversation Mode - TO-DO */}
        <Tippy content={isConversationMode ? "Disable Conversation Mode" : "Enable Conversation Mode"}>
          <button 
            onClick={isConversationMode ? handleDisableConversationMode : () => setIsConversationMode(true)} 
            className={`toolbar-button ${isConversationMode ? 'active' : ''}`}
          >
            <FontAwesomeIcon icon={isConversationMode ? faMicrophoneSlash : faMicrophone} />
            </button>
          </Tippy>
        </div>
      </div>
    </div>
  );

  // Update message rendering to include disclaimer and redirect styling
  const renderMessage = (message, index) => {
    // Hide copy/ratings for the welcome message (first bot message only)
    const isWelcome = index === 0 && message.type === 'bot' && messages.length === 1;
    const isRedirect = message.isRedirectMessage;
    
    return (
      <div 
        key={index} 
        className={`message ${message.type}`}
        data-redirect={isRedirect || undefined}
      >
        {message.type === 'user' ? (
          <div className="user-message-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={components}
              className="markdown-content"
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={components}
              className="markdown-content"
            >
              {message.content}
            </ReactMarkdown>
            
           
            
            {!isWelcome && !isRedirect && (
              <div className="message-actions">
                <button
                  className={`message-action-button ${copiedMessageId === index ? 'copy-success' : ''}`}
                  onClick={() => handleCopy(index, message.content)}
                >
                  {copiedMessageId === index ? (
                    <>
                      <i className="fas fa-check"></i>
                      Copied!
                    </>
                  ) : (
                    <>
                      <i className="fas fa-copy"></i>
                      Copy
                    </>
                  )}
                </button>
                {message.type === 'bot' && (
                  <div className="rating-buttons">
                    <button
                      className={`rating-button thumbs-up ${messageRatings[index] === 'positive' ? 'active' : ''}`}
                      onClick={() => handleRating(index, 'positive')}
                      title="Thumbs up"
                    >
                      <i className="fas fa-thumbs-up"></i>
                    </button>
                    <button
                      className={`rating-button thumbs-down ${messageRatings[index] === 'negative' ? 'active' : ''}`}
                      onClick={() => handleRating(index, 'negative')}
                      title="Thumbs down"
                    >
                      <i className="fas fa-thumbs-down"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Notify parent when related questions change
  useEffect(() => {
    if (onRelatedQuestionsUpdate) {
      onRelatedQuestionsUpdate(conversationContext.relatedQuestions || []);
    }
  }, [conversationContext.relatedQuestions, onRelatedQuestionsUpdate]);

  // Notify parent when topics change
  useEffect(() => {
    if (onTopicsUpdate) {
      onTopicsUpdate(conversationContext.topics || []);
    }
  }, [conversationContext.topics, onTopicsUpdate]);

  // Add topics display component
  const TopicsDisplay = () => {
    if (!conversationContext.topics?.length) return null;

    const handleHeaderClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setOpenSection(openSection === 'topics' ? null : 'topics');
    };

    const handleTopicClick = (e, topic) => {
      e.preventDefault();
      e.stopPropagation();
      const query = `Tell me about ${topic}`;
      setInput(query);
      handleSubmit({ preventDefault: () => { } });
    };

    return (
      <div className="topics-display" onClick={(e) => e.stopPropagation()}>
        <div
          className={`topics-header ${openSection !== 'topics' ? 'collapsed' : ''}`}
          onClick={handleHeaderClick}
        >
          <h4>Topics ({conversationContext.topics.length})</h4>
        </div>
        <div className={`topics-list ${openSection !== 'topics' ? 'collapsed' : ''}`}>
          {conversationContext.topics.map((topic, index) => (
            <span
              key={index}
              className="topic-tag"
              onClick={(e) => handleTopicClick(e, topic)}
            >
              {topic}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Add SavedChatsModal component
  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation(); // Prevent chat from being loaded when deleting
    try {
      // Get current chats
      const currentChats = await loadChats();
      // Filter out the deleted chat
      const updatedChats = currentChats.filter(chat => chat.id !== chatId);
      // Save the updated chats back to storage
      localStorage.setItem('saved_chats', JSON.stringify(updatedChats));
      // Update the UI
      setSavedChats(updatedChats);
      setHasSavedChats(updatedChats.length > 0);
    } catch (error) {
      // Error deleting chat
    }
  };

  const SavedChatsModal = () => {
    if (!showChatModal) return null;

    return (
      <div className="chat-modal" onClick={() => setShowChatModal(false)}>
        <div className="chat-modal-content" onClick={e => e.stopPropagation()}>
          <div className="chat-modal-header">
            <h3>Saved Chats</h3>
            <button className="close-button" onClick={() => setShowChatModal(false)}>×</button>
          </div>
          <div className="chat-modal-disclaimer">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Chats are stored locally in your browser and not on our servers. Clear your browser data or click the delete icon to remove saved conversations.</span>
          </div>
          <div className="chat-list">
            {savedChats.length > 0 ? (
              savedChats.map((chat, index) => (
                <div key={index} className="chat-item">
                  <div className="chat-item-content" onClick={() => handleLoadChat(chat.id)}>
                    <div className="chat-item-title">
                      {chat.title || `Chat ${index + 1}`}
                    </div>
                    <div className="chat-item-date">
                      {new Date(chat.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <button
                    className="chat-item-delete"
                    onClick={(e) => handleDeleteChat(e, chat.id)}
                    title="Delete chat"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              ))
            ) : (
              <div className="no-chats">No saved chats found</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DocumentQuickActions = () => {
    if (!documentContext?.isDocumentContext) return null;

    const documentActions = [
      {
        title: "What are the key points?",
        icon: <FontAwesomeIcon icon={faKey} size="sm" />,
        query: "What are the key points?"
      },
      {
        title: "What are the main arguments?",
        icon: <FontAwesomeIcon icon={faQuestionCircle} size="sm" />,
        query: "What are the main arguments being put forward?"
      },
      {
        title: "Summarize this document",
        icon: <FontAwesomeIcon icon={faFileAlt} size="sm" />,
        query: "Can you provide a summary?"
      },
    ];

    const handleActionClick = (query) => {
      setUsedDocumentActions(prev => new Set([...prev, query]));
      handleQuickAction(query);
    };

    return (
      <div className="document-suggestions-container">
        {externalShowDocumentSuggestions && showDocumentSuggestions && (
          <>
            {usedDocumentActions.size < 3 && (
              <button 
                className="toggle-suggestions-button"
                onClick={() => setShowDocumentSuggestions(false)}
                title="Hide suggestions"
              >
                <FontAwesomeIcon icon={faTimes} size="sm" />
              </button>
            )}
            <div className={`suggestion-boxes${isOnDairePage ? ' is-on-daire-page' : ''}`}>
              {documentActions.map((action, index) => (
                !usedDocumentActions.has(action.query) && (
                  <button
                    key={index}
                    className="suggestion-box"
                    onClick={() => handleActionClick(action.query)}
                  >
                    <span className="suggestion-icon">{action.icon}</span>
                    <span className="suggestion-text">{action.title}</span>
                  </button>
                )
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Add this effect to handle state persistence
  useEffect(() => {
    if (messages.length > 0) {
      // Save chat state when messages change
      const chatState = {
        messages,
        conversationContext,
        currentDocuments,
        lastSearchResults
      };
      sessionStorage.setItem('daireChat_' + documentContext?.name, JSON.stringify(chatState));
    }
  }, [messages, conversationContext, currentDocuments, lastSearchResults, documentContext?.name]);

  // Add a helper function to check if file is PDF (can be placed with other utility functions)
  const isPDF = (filename) => {
    if (!filename) return false;
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension === 'pdf';
  };

  // Add the openPDFModal function
  const openPDFModal = (id, title, fileName) => {
    const maxZIndex = Math.max(...Object.values(modals).map(modal => modal.zIndex || 0), 0) + 1;
    setModals(prev => ({
      ...prev,
      [id]: {
        id,
        isOpen: true,
        title,
        pdfUrl: fileName,
        top: `${15 + (Object.keys(prev).length * 2)}%`,
        left: `${30 + (Object.keys(prev).length * 2)}%`,
        zIndex: maxZIndex
      }
    }));
  };

  // Add the closePDFModal function
  const closePDFModal = (id) => {
    setModals(prevModals => {
      const newModals = { ...prevModals };
      if (newModals[id]) {
        newModals[id].isOpen = false;
      }
      return newModals;
    });
  };

  // Updated to use clean NewsModal API - just pass the ID and type
  const handleNewsShow = (newsItem) => {
    const newsId = newsItem.documentId || newsItem.listItemId || newsItem.id;
    const articleType = newsItem.type === 'Submitted Article' ? 'submitted' : 'news';
    setActiveNewsId(newsId);
    setActiveNewsType(articleType);
    setShowNewsModal(true);
  };

  // Add this function to format the welcome message with placeholders
  const formatWelcomeMessage = (message, user, profileData) => {

    if (!message) return '';

    const formattedName = getUserFirstName();
    const fullName = getUserDisplayName();
    const lastName = profileData?.name ? (profileData.name.split(' ')[1] || '') : (user?.profile.name?.split(' ')[1] || '');

    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
    const currentYear = currentDate.getFullYear();
    const priorMonthDate = new Date(currentDate);
    priorMonthDate.setMonth(currentDate.getMonth() - 1);
    const priorMonthName = priorMonthDate.toLocaleString('default', { month: 'long' });
    return message
      .replaceAll('MemberFirstName', formattedName)
      .replaceAll('MemberLastName', lastName)
      .replaceAll('MemberFullName', fullName || 'there')
      .replaceAll('CurrentMonth', currentMonth)
      .replaceAll('CurrentYear', currentYear)
      .replaceAll('PriorMonth', priorMonthName)
  };

  // Get disclaimers from daireOptionsData
  const getDisclaimer = (disclaimerName) => {
    const disclaimer = daireOptionsData?.find(option => option.title === disclaimerName);
    return disclaimer?.value || '';
  };

  const handleRating = async (messageId, rating) => {
    try {
        // Get the message that's being rated
        const messageToRate = messages[messageId];
        if (!messageToRate) {
            return;
        }

        // Use the listId from the message if available, otherwise use the document context listItemId
        const listId = messageToRate.listId || (documentContext?.isDocumentContext ? documentContext.listItemId : null);
        if (!listId) {
            return;
        }
        await apiHelper.updateChatRating(messageId, rating, user?.profile?.email, listId);
        setMessageRatings(prev => ({
            ...prev,
            [messageId]: rating
        }));
        // Show a toast notification
        toast.success(`Rating ${rating === 'positive' ? '👍' : '👎'} saved!`, {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    } catch (error) {
        toast.error('Failed to save rating');
    }
};

  // Add this function to handle sources toggle
  const handleSourcesToggle = () => {
    setShowSources(!showSources);
    // If we're showing sources, wait for state update and then scroll
    if (!showSources) {
      setTimeout(() => {
        sourcesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Helper to create a signature for the sources array
  const getSourcesSignature = (sources) => {
    if (!sources || sources.length === 0) return '';
    return sources.map(s => s.title + (s.publishDate || '').toString()).join('|');
  };

  // Flash the Show Sources button when new sources are loaded
  useEffect(() => {
    const signature = getSourcesSignature(allDocumentSources);
    if (signature && signature !== lastSourcesSignature.current) {
      setFlashSourcesButton(false); // Reset in case it's still true
      setTimeout(() => setFlashSourcesButton(true), 10); // Small delay to retrigger animation
      setTimeout(() => setFlashSourcesButton(false), 3010); // Match the animation duration
      lastSourcesSignature.current = signature;
    }
  }, [allDocumentSources]);

  // Add scroll handler to detect when user is near bottom
  const handleScroll = () => {
    if (chatWindowRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
      const atBottom = scrollHeight - scrollTop - clientHeight < 30;
      setUserScrolledUp(!atBottom);
    }
  };

  // Add effect to handle scrolling
  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    if (chatWindow) {
      chatWindow.addEventListener('scroll', handleScroll);
      return () => chatWindow.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Update the scroll effect for new messages
  useEffect(() => {
    if (chatWindowRef.current && !userScrolledUp) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages, userScrolledUp]);

  // Add scroll to bottom button
  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTo({
        top: chatWindowRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setUserScrolledUp(false);
      userWasAtBottomRef.current = true;
    }
  };

  // Update the getSourceCounts function
  const getSourceCounts = () => {
    const counts = {
      all: 0,
      news: 0,
      repository: 0,
      workingGroups: 0,
      events: 0
    };

    allDocumentSources.forEach(doc => {
      if (doc.score >= 0.8) {
        counts.all++;
        if (doc.type === 'Working Group') {
          counts.workingGroups++;
        } else if (doc.type === 'Event' || doc.eventDate || doc.EventDate) {
          counts.events++;
        } else if (doc.type === 'News' || doc.publishDate || doc.type === 'Submitted Article') {
          counts.news++;
        } else {
          counts.repository++;
        }
      }
    });
    return counts;
  };

  // Update the getFilteredSources function
  const getFilteredSources = () => {
    const filtered = allDocumentSources.filter(doc => {
      // Check if document should be excluded from sources
      if (doc.doNotShowInSources === true || doc.doNotShowInSources === 'true') {
        return false;
      }
      const meetsScoreThreshold = doc.score >= 15;
      if (!meetsScoreThreshold) return false;
      if (selectedSourceType === 'workingGroups') {
        return doc.type === 'Working Group';
      }
      if (selectedSourceType === 'events') {
        return doc.type === 'Event' || doc.eventDate || doc.EventDate;
      }
      if (selectedSourceType === 'news') {
        const isNews = doc.type === 'News' || doc.publishDate || doc.type === 'Submitted Article';
        return isNews;
      }
      if (selectedSourceType === 'repository') {
        const isRepo = doc.type !== 'News' && !doc.publishDate && doc.type !== 'Working Group' && doc.type !== 'Event' && !doc.eventDate && !doc.EventDate && doc.type !== 'Submitted Article';
        return isRepo;
      }
      return true;
    });
    return filtered;
  };

  // Update the score display thresholds
  const getScoreClass = (score) => {
    if (score >= 70) return "high";
    if (score >= 35) return "medium";
    return "low";
  };


  const stripHtml = (html) => {
    if (!html) return "No content available";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    // Get the text content
    let text = tmp.textContent || tmp.innerText || "No content available";
    // Replace HTML entities with spaces
    text = text.replace(/&#58;/g, ' ');
    // Replace multiple spaces with a single space
    text = text.replace(/\s+/g, ' ');
    // Add space after dates (assuming format like "21 May 2025")
    text = text.replace(/(\d{1,2}\s+[A-Za-z]+\s+\d{4})([A-Za-z])/g, '$1 $2');
    // Add space after periods followed by capital letters
    text = text.replace(/\.([A-Z])/g, '. $1');
    // Add space after years followed by text
    text = text.replace(/(\d{4})([A-Za-z])/g, '$1 $2');
    return text.trim();
  };

  // Add this helper function near the top of the component
  const formatSourceText = (text) => {
    if (!text) return '';
    // Create a temporary element to decode HTML entities
    const tmp = document.createElement("DIV");
    tmp.innerHTML = text;
    let processedText = tmp.textContent || tmp.innerText || '';
    // Then strip HTML tags
    processedText = processedText.replace(/<[^>]*>/g, '');
    // Replace HTML entities with spaces
    processedText = processedText.replace(/&#58;/g, ' ');
    // Remove unprintable characters and replace with spaces
    processedText = processedText.replace(/[\u0000-\u001F\u007F-\u009F\uFFFD]/g, ' ');
    // Remove any remaining square bracket characters that might be causing issues
    processedText = processedText.replace(/[\[\]]/g, ' ');
    // Replace multiple spaces with a single space
    processedText = processedText.replace(/\s+/g, ' ');
    // Add space after dates (assuming format like "21 May 2025")
    processedText = processedText.replace(/(\d{1,2}\s+[A-Za-z]+\s+\d{4})([A-Za-z])/g, '$1 $2');
    // Add space after periods followed by capital letters
    processedText = processedText.replace(/\.([A-Z])/g, '. $1');
    // Add space after the title (assuming it ends with a year)
    processedText = processedText.replace(/(\d{4})([A-Za-z])/g, '$1 $2');
    return processedText.trim();
  };



  // Update effect to handle document accumulation
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.documents) {
        setAllDocumentSources(prevSources => {
          const newSources = [...prevSources];
          lastMessage.documents.forEach(doc => {
            if (!newSources.some(existingDoc => existingDoc.title === doc.title)) {
              // Extract filename from URL if it exists
              let fileName = doc.fileName;
              if (fileName && fileName.includes('/')) {
                fileName = decodeURIComponent(fileName.split('/').pop());
              }
              newSources.push({
                ...doc,
                fileName: fileName || doc.title,
                listItemId: doc.listItemId || doc.title
              });
            }
          });
          const sortedSources = newSources.sort((a, b) => (b.score || 0) - (a.score || 0));
          return sortedSources;
        });
      }
    }
  }, [messages]);

  // Auto-scroll to bottom for fullPage mode
  useEffect(() => {
    if (!fullPage) return;
    // Only scroll if user is near the bottom
    const threshold = 200; // px
    const scrollPos = window.scrollY + window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;
    const nearBottom = docHeight - scrollPos < threshold;
    if (nearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, fullPage]);

  // Notify parent when updating summary state changes
  useEffect(() => {
    if (onUpdatingSummaryChange) {
      onUpdatingSummaryChange(isUpdatingSummary);
    }
  }, [isUpdatingSummary, onUpdatingSummaryChange]);

  // Notify parent when messages change
  useEffect(() => {
    if (onMessagesUpdate) {
      onMessagesUpdate(messages);
    }
  }, [messages, onMessagesUpdate]);

  useEffect(() => {
    if (scrollToBottomNext) {
      if (chatWindowRef.current) {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      }
      setScrollToBottomNext(false);
    }
  }, [messages, scrollToBottomNext]);

  useImperativeHandle(ref, () => ({
    sendQuestionToChat: (question) => {
      setInput(question); // for UI
      handleSubmit(null, question); // always submit immediately
    },
    scrollToBottom: () => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }));

  // Add this helper function near the top of the component
  const formatWorkingGroupText = (text) => {
    if (!text) return '';
    // Find the last occurrence of ]
    const lastBracketIndex = text.lastIndexOf(']');
    if (lastBracketIndex === -1) return text;
    // Return everything after the last ]
    return text.slice(lastBracketIndex + 1).trim();
  };

  // Preview modal functions (same as WGVisual.js)
  const showPreviewModal = (groupData, type = 'workingGroup') => {
    setPreviewModal({
      show: true,
      data: groupData,
      type: type
    });
  };

  const closePreviewModal = () => {
    setPreviewModal({
      show: false,
      data: null,
      type: 'workingGroup'
    });
  };

  // Update the handleWorkingGroupShow function to use preview modal
  const handleWorkingGroupShow = (workingGroup) => {
    // Show preview modal instead of full details
    showPreviewModal(workingGroup, 'workingGroup');
  };



  // Define speakText first
  const speakText = (text) => {
    return new Promise((resolve) => {
      if (!isConversationModeRef.current) {
        return resolve();
      }
      try {
        const audioConfig = AudioConfig.fromDefaultSpeakerOutput();
        const speechConfig = SpeechConfig.fromSubscription(
          process.env.REACT_APP_SPEECH_KEY,
          process.env.REACT_APP_SPEECH_REGION
        );
        speechConfig.speechSynthesisVoiceName = "en-IE-ConnorNeural";
        const synthesizer = new SpeechSynthesizer(speechConfig, audioConfig);
        activeSynthRef.current = synthesizer;

        synthesizer.speakTextAsync(
          text,
          (result) => {
            synthesizer.close();
            activeSynthRef.current = null;
            resolve();
          },
          (error) => {
            synthesizer.close();
            activeSynthRef.current = null;
            resolve();
          }
        );
      } catch (error) {
        if (activeSynthRef.current) activeSynthRef.current = null;
        resolve();
      }
    });
  };



  useEffect(() => {
    if (!isConversationMode && activeSynthRef.current) {
      try {
        activeSynthRef.current.close();
        activeSynthRef.current = null;
      } catch (e) {
        // Error cancelling speech
      }
    }
  }, [isConversationMode]);

  const handleDisableConversationMode = () => {
    setIsConversationMode(false);
    if (activeSynthRef.current) {
      try {
        activeSynthRef.current.close();
        activeSynthRef.current = null;
      } catch (e) {
        // Error cancelling speech
      }
    }
  };

  // Enhanced source type mapping with IFIA brand colors
  const getSourceTypeInfo = (doc) => {
    if (doc.type === 'Event' || doc.eventDate || doc.EventDate) {
      return { 
        type: 'event', 
        icon: faCalendarAlt, 
        color: '#bd2130', // --ifia-red
        bgColor: '#fdf2f2',
        label: 'Event',
        textColor: '#bd2130'
      };
    }
    if (doc.type === 'Working Group') {
      return { 
        type: 'working-group', 
        icon: faUsers, 
        color: '#006d92', // --ifia-blue
        bgColor: '#f0f8fb',
        label: 'Working Group',
        textColor: '#006d92'
      };
    }
    // Check for submitted articles first
    if (doc.type === 'Submitted Article') {
      return { 
        type: 'submitted-article', 
        icon: faNewspaper, 
        color: '#8b5cf6', // Purple for submitted articles
        bgColor: '#f3f4f6',
        label: 'Submitted Article',
        textColor: '#8b5cf6'
      };
    }
    if (doc.type === 'News' || doc.publishDate) {
      return { 
        type: 'news', 
        icon: faNewspaper, 
        color: '#eb7b15', // --ifia-orange
        bgColor: '#fef7f0',
        label: 'News',
        textColor: '#eb7b15'
      };
    }
    return { 
      type: 'repository', 
      icon: faFileAlt, 
      color: '#56b32f', // --ifia-green
      bgColor: '#f3f8f3', // --bs-soft-primary
      label: 'Repository',
      textColor: '#385724' // --ifia-dark-green
    };
  };

  // Enhanced score display with IFIA brand colors
  const getScoreIndicator = (score) => {
    if (score >= 80) return { class: 'score-excellent', icon: faCheckCircle, color: '#56b32f' }; // --ifia-green
    if (score >= 60) return { class: 'score-good', icon: faCheckCircle, color: '#eb7b15' }; // --ifia-orange
    if (score >= 40) return { class: 'score-fair', icon: faExclamationCircle, color: '#006d92' }; // --ifia-blue
    return { class: 'score-low', icon: faTimesCircle, color: '#bd2130' }; // --ifia-red
  };

  return (
    <div className={`daira-chat${isOnDairePage ? ' is-on-daire-page' : ''}${!isOnDairePage ? ' daire-sidebar' : ''}${isFullscreen ? ' fullscreen' : ''}`}>
      <ToastContainer />
      {renderToolbar()}
      <div className={`chat-window${isFullscreen ? ' fullpage' : ''}`} ref={chatWindowRef}>
        {messages.map((message, index) => renderMessage(message, index))}
        <div ref={bottomRef} />
        {!isNearBottom && (
          <button
            className="scroll-bottom-button"
            onClick={scrollToBottom}
            title="Scroll to bottom"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 13l-7 7-7-7m14-8l-7 7-7-7" />
            </svg>
          </button>
        )}
        {allDocumentSources.length > 0 && showSources && (
          <div className="sources-section-enhanced" ref={sourcesRef}>
            <div className="sources-header-enhanced">
              <div className="sources-title">
                <FontAwesomeIcon icon={faSearch} className="sources-main-icon" />
                <h3>Source Documents</h3>
                <span className="sources-count">{getFilteredSources().length} found</span>
              </div>
              <div className="sources-type-filter-enhanced">
                <button
                  className={`type-filter-btn modern ${selectedSourceType === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedSourceType('all')}
                >
                  <FontAwesomeIcon icon={faGlobe} />
                  <span>All Sources</span>
                  <span className="filter-count">{getSourceCounts().all}</span>
                </button>
                <button
                  className={`type-filter-btn modern ${selectedSourceType === 'news' ? 'active' : ''}`}
                  onClick={() => setSelectedSourceType('news')}
                >
                  <FontAwesomeIcon icon={faNewspaper} />
                  <span>News</span>
                  <span className="filter-count">{getSourceCounts().news}</span>
                </button>
                <button
                  className={`type-filter-btn modern ${selectedSourceType === 'events' ? 'active' : ''}`}
                  onClick={() => setSelectedSourceType('events')}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <span>Events</span>
                  <span className="filter-count">{getSourceCounts().events}</span>
                </button>
                <button
                  className={`type-filter-btn modern ${selectedSourceType === 'workingGroups' ? 'active' : ''}`}
                  onClick={() => setSelectedSourceType('workingGroups')}
                >
                  <FontAwesomeIcon icon={faUsers} />
                  <span>Groups</span>
                  <span className="filter-count">{getSourceCounts().workingGroups}</span>
                </button>
                <button
                  className={`type-filter-btn modern ${selectedSourceType === 'repository' ? 'active' : ''}`}
                  onClick={() => setSelectedSourceType('repository')}
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                  <span>Repository</span>
                  <span className="filter-count">{getSourceCounts().repository}</span>
                </button>
              </div>
            </div>
            
            <div className="sources-grid-enhanced">
              {getFilteredSources().slice(0, visibleSources).map((doc, index) => {
                const typeInfo = getSourceTypeInfo(doc);
                const scoreInfo = getScoreIndicator(doc.score);
                
                return (
                  <div
                    key={index}
                    className={`source-card-enhanced ${typeInfo.type} clickable`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (typeInfo.type === 'event') {
                        const formattedEvent = {
                          ...doc,
                          id: doc.listItemId || doc.documentId || doc.id,
                          listItemId: doc.listItemId || doc.documentId || doc.id,
                          Topics: Array.isArray(doc.topics) ? doc.topics : 
                                 (doc.topics ? [doc.topics] : 
                                 Array.isArray(doc.Topics) ? doc.Topics :
                                 (doc.Topics ? [doc.Topics] : []))
                        };
                        setActiveEvent(formattedEvent);
                        setShowEventModal(true);
                      } else if (doc.type === 'Working Group') {
                        handleWorkingGroupShow(doc);
                      } else if (doc.type === 'Repository' || doc.type === 'Publications & Presentations') {
                        openPDFModal(doc.listItemId, doc.title, doc.fileName);
                      } else if (doc.type === 'Submitted Article') {
                        handleNewsShow(doc);
                      } else if (doc.publishDate || doc.type === 'News') {
                        handleNewsShow(doc);
                      }
                    }}
                  >
                    <div className="source-card-header">
                      <div className="source-type-badge" style={{ backgroundColor: typeInfo.bgColor, color: typeInfo.textColor }}>
                        <FontAwesomeIcon icon={typeInfo.icon} />
                        <span>{typeInfo.label}</span>
                      </div>
                      <div className="source-score-badge" style={{ color: scoreInfo.color }}>
                        <FontAwesomeIcon icon={scoreInfo.icon} />
                        <span>{Math.round(doc.score)}%</span>
                      </div>
                      
                    </div>
                    
                    <div className="source-card-content">
                      <h4 className="source-title-enhanced">
                        {formatSourceText(doc.title)}
                        {isPDF(doc.fileName) && (
                          <FontAwesomeIcon icon={faFilePdf} className="pdf-indicator" />
                        )}
                      </h4>
                      
                      {(doc.publishDate || doc.eventDate || doc.EventDate) && (
                        <div className="source-date-enhanced">
                          <FontAwesomeIcon icon={faClock} />
                          <span>{new Date(doc.publishDate || doc.eventDate || doc.EventDate).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}</span>
                        </div>
                      )}
                      
                      {doc.type === 'Submitted Article' && (doc.submittedAuthor || doc.submittedCompany) && (
                        <div className="source-author-enhanced">
                          <FontAwesomeIcon icon={faUsers} />
                          <span>
                            {doc.submittedAuthor && doc.submittedCompany ? 
                              `${doc.submittedAuthor} (${doc.submittedCompany})` :
                              doc.submittedAuthor || doc.submittedCompany
                            }
                          </span>
                        </div>
                      )}
                      
                      <div className="source-description-enhanced">
                        {stripHtml(formatSourceText((doc.summary || "").trim() || "No summary available"))}
                      </div>
                    </div>
                    
                    <div className="source-card-footer">
                      <div className="source-relevance">
                        <span className="relevance-label">Relevance</span>
                        <div className="relevance-bar">
                          <div 
                            className="relevance-fill" 
                            style={{ 
                              width: `${Math.min(doc.score, 100)}%`,
                              backgroundColor: scoreInfo.color
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="source-action-hint">
                        <FontAwesomeIcon icon={faMousePointer} />
                        <span>Click to view</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {visibleSources < getFilteredSources().length && (
                <div className="show-more-card">
                  <button
                    className="show-more-btn-enhanced"
                    onClick={e => {
                      e.preventDefault();
                      setVisibleSources(v => Math.min(v + 5, getFilteredSources().length));
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronDown} />
                    <span>Show {Math.min(5, getFilteredSources().length - visibleSources)} more sources</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* PDFModal components */}
        {Object.values(modals).map((modal) => (
          modal.isOpen && (
            <PDFModal
              key={modal.id}
              title={modal.title}
              url={modal.pdfUrl}
              show={modal.isOpen}
              onClose={(e) => {
                e.stopPropagation();
                closePDFModal(modal.id);
              }}
              allowFullscreen={true}
              allowMove={true}
              showLinkinToolBar={true}
              style={{ top: modal.top, left: modal.left, zIndex: modal.zIndex }}
              listItemId={modal.id}
            />
          )
        ))}
        {!isProcessing && !documentContext?.isDocumentContext && showQuickActionsInternal && <QuickActions />}
        {!isProcessing && documentContext?.isDocumentContext && <DocumentQuickActions />}
        <StreamingStatus state={streamingState} />
      </div>
      <form onSubmit={handleSubmit} className="input-form">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (e.target.value && !documentContext?.isDocumentContext) {
              setShowQuickActionsInternal(false);
              setShowSources(false);
            }
          }}
          placeholder={documentContext?.isDocumentContext ?
            `Ask about "${documentContext.title}"...` :
            "Ask Daire..."}
          disabled={isProcessing}
        />
        {isConversationMode && (
          <button
            type="button"
            onClick={startListening}
            className={`voice-button ${isListening ? 'listening' : ''}`}
            disabled={isProcessing || isListening}
          >
            <FontAwesomeIcon icon={isListening ? faMicrophoneSlash : faMicrophone} />
          </button>
        )}
        <button
          type="submit"
          disabled={isProcessing || !input.trim()}
          className={`send-button ${isProcessing ? 'processing' : ''}`}
        >
          {isProcessing ? (
            <div className="stop-button" onClick={handleStop}>
              <FontAwesomeIcon icon={faStop} />
            </div>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </>
          )}
        </button>
        {allDocumentSources.length > 0 && (
          <button
            type="button"
            onClick={handleSourcesToggle}
            className={`sources-button${showSources ? ' active' : ''}${flashSourcesButton ? ' flash' : ''}`}
            title={showSources ? "Hide Sources" : "Show Sources"}
            disabled={isProcessing}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          </button>
        )}
      </form>
      {messages.length > 1 && (
        <div className="daire-ai-notice-wrapper">
          {isSidebar ? (
            <div className="daire-ai-notice daire-notice-compact">
              <div className="daire-notice-header">
                <div className="daire-notice-icon-container">
                  <FontAwesomeIcon icon={faExclamationCircle} className="daire-notice-icon"  />
                </div>
                <span className="daire-notice-text" dangerouslySetInnerHTML={{ __html: getDisclaimer('Disclaimer1')  }} />
                <button 
                  className="daire-notice-expand-btn"
                  onClick={() => setShowFullDisclaimer(!showFullDisclaimer)}
                  title={showFullDisclaimer ? "Show less" : "Show more"}
                >
                  <FontAwesomeIcon icon={showFullDisclaimer ? faChevronUp : faChevronDown} />
                </button>
                <button 
                  className="daire-notice-modal-btn"
                  onClick={() => setShowDaireInfoModal(true)}
                  title="View additional disclaimer"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </button>
              </div>
              {showFullDisclaimer && (
                <div className="daire-notice-details" dangerouslySetInnerHTML={{ __html: getDisclaimer('Disclaimer1.2') }} />
              )}
            </div>
          ) : (
            <div className="daire-ai-notice daire-notice-full">
              <div className="daire-notice-layout">
                
                <div className="daire-notice-content">
                  <div className="daire-notice-primary-text" dangerouslySetInnerHTML={{ __html: getDisclaimer('Disclaimer1') }} />
                  <div className="daire-notice-secondary-text" dangerouslySetInnerHTML={{ __html: getDisclaimer('Disclaimer1.2') }} />
                </div>
                <button 
                  className="daire-notice-modal-btn-full"
                  onClick={() => setShowDaireInfoModal(true)}
                  title="View additional disclaimer"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <SavedChatsModal />
      
      {/* Preview Modal (same as WGVisual.js) */}
      {previewModal.show && (
        <div className="preview-modal-overlay" onClick={closePreviewModal}>
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{previewModal.data?.title || previewModal.data?.Title}</h3>
              <button className="preview-modal-close" onClick={closePreviewModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="preview-modal-body">
              <div className="preview-badge">
                <i className="fas fa-flask"></i>
                Preview Mode
              </div>
              <p>
                {previewModal.type === 'steeringGroup' 
                  ? 'Steering Group details and functionality will be available in September 2025.' 
                  : 'Working Group details including Terms of Reference, Meeting Minutes, Team Members, and Reference Documents will be available in September 2025.'
                }
              </p>
              <div className="preview-features">
                <h4>Coming Soon:</h4>
                <ul>
                  {previewModal.type === 'workingGroup' ? (
                    <>
                      <li><i className="fas fa-file-alt"></i> Terms of Reference</li>
                      <li><i className="fas fa-users"></i> Meet the Team</li>
                      <li><i className="fas fa-calendar"></i> Meeting Minutes</li>
                      <li><i className="fas fa-folder"></i> Reference Documents</li>
                    </>
                  ) : (
                    <>
                      <li><i className="fas fa-compass"></i> Steering Committee Details</li>
                      <li><i className="fas fa-users"></i> Committee Members</li>
                      <li><i className="fas fa-bullseye"></i> Strategic Objectives</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
            <div className="preview-modal-footer">
              <button className="btn btn-secondary" onClick={closePreviewModal}>
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* NewsModal component */}
      {activeNewsId && (
        <NewsModal
          articleId={activeNewsId}
          articleType={activeNewsType}
          show={showNewsModal}
          onClose={() => {
            setShowNewsModal(false);
            setActiveNewsId(null);
            setActiveNewsType('news');
          }}
          allowFullscreen={true}
        />
      )}
      {activeWorkingGroup && showWorkingGroupModal && (
        <div 
          className="wg-modal-backdrop"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowWorkingGroupModal(false);
          }}
        >
          <div 
            className="wg-modal"
            tabIndex="-1" 
            role="dialog" 
            onClick={e => e.stopPropagation()}
          >
            <div className="wg-modal-content">
              <div className="wg-modal-header">
                <h5 className="wg-modal-title">{activeWorkingGroup.title || 'Working Group'}</h5>
                <button 
                  type="button" 
                  className="wg-btn-close" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowWorkingGroupModal(false);
                  }}
                >×</button>
              </div>
              <div className="wg-modal-body">
                <div className="wg-working-group-info">
                  {activeWorkingGroup.workingGroupType && (
                    <div className="wg-info-section">
                      <h6>Type</h6>
                      <p>{activeWorkingGroup.workingGroupType}</p>
                    </div>
                  )}
                  {activeWorkingGroup.steeringGroup && activeWorkingGroup.steeringGroup !== 'N/a' && (
                    <div className="wg-info-section">
                      <h6>Steering Group</h6>
                      <p>{activeWorkingGroup.steeringGroup}</p>
                    </div>
                  )}
                  {activeWorkingGroup.startDate && (
                    <div className="wg-info-section">
                      <h6>Timeline</h6>
                      <p>{activeWorkingGroup.startDate} - {activeWorkingGroup.endDate || 'Present'}</p>
                    </div>
                  )}
                  {activeWorkingGroup.hasTOR && (
                    <div className="wg-info-section">
                      <h6>Terms of Reference</h6>
                      <p>Available ({activeWorkingGroup.torObjectiveCount} objectives)</p>
                    </div>
                  )}
                  {activeWorkingGroup.updateCount > 0 && (
                    <div className="wg-info-section">
                      <h6>Updates</h6>
                      <p>{activeWorkingGroup.updateCount} updates available</p>
                    </div>
                  )}
                </div>
                <div className="wg-content-section mt-4">
                  <div dangerouslySetInnerHTML={{ __html: formatWorkingGroupOverview(activeWorkingGroup.text || activeWorkingGroup.summary || 'No summary available') }} />
                </div>
              </div>
              <div className="wg-modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowWorkingGroupModal(false);
                  }}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Use the id property since that's what we have in the data
                    const workingGroupId = activeWorkingGroup.listItemId || activeWorkingGroup.id;
                    if (workingGroupId) {
                      window.location.href = `/WorkingGroups/WorkingGroup/${workingGroupId}`;
                    } else {
                      toast.error('Unable to navigate to working group details');
                    }
                    setShowWorkingGroupModal(false);
                  }}
                >
                  View Full Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEventModal && activeEvent && (
        <EventsModal
          data={activeEvent}
          show={showEventModal}
          onClose={() => setShowEventModal(false)}
          allowFullscreen={true}
        />
      )}
      
      {/* Disclaimer2 Modal */}
      {showDisclaimer2Modal && (
        <div className="disclaimer-modal-overlay">
          <div className="disclaimer-modal-content">
            <div className="disclaimer-modal-header">
              <h3>Additional Disclaimer</h3>
              <button 
                className="disclaimer-modal-close" 
                onClick={() => setShowDisclaimer2Modal(false)}
                title="Close disclaimer"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="disclaimer-modal-body">
              <div 
                className="disclaimer-modal-text" 
                dangerouslySetInnerHTML={{ 
                  __html: getDisclaimer('Disclaimer2') || 'Additional disclaimer information will be displayed here.' 
                }} 
              />
            </div>
            <div className="disclaimer-modal-footer">
              <button 
                className="btn btn-primary" 
                onClick={() => setShowDisclaimer2Modal(false)}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Daire Info Modal */}
      {showDaireInfoModal && (
        <InfoModal
          title="Daire Disclaimer"
          data={getDisclaimer('Disclaimer2')}
          show={showDaireInfoModal}
          onClose={() => setShowDaireInfoModal(false)}
          small={true}
        />
      )}
    </div>
  );
});

export default DaireChat; 
